import { transparentize } from "polished";
import styled, { keyframes } from "styled-components";

import { ButtonContainer } from "@/components/Button/styles";
import QRScanner from "@/components/QRScanner";

export const QRScannerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.background};
`;

export const QRScannerVideo = styled(QRScanner)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const QRScannerOverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  & > svg.outline {
    position: absolute;
    fill: white;
  }
`;

const QRScannerLineAnimation = keyframes`
  from {
    transform: translateY(-125px);
  }
  50% {
    transform: translateY(125px);
  }
  to {
    transform: translateY(-125px);
  }
`;

const QRScannerLaserBefore = keyframes`
  from {
    height: 150px;
    transform: translateY(-250px);
  }
  30% {
    height: 60px;
  }
  50% {
    height: 0;
    transform: translateY(0px);
  }
`;

const QRScannerLaserAfter = keyframes`
  from {
    height: 0;
  }
  50% {
    height: 0;
  }
  50.1% {
    height: 150px;
    transform: translateY(250px);
  }
  70% {
    height: 60px;
  }
  to {
    height: 0;
    transform: translateY(0px);
  }
`;

export const QRScannerLaser = styled.div`
  position: absolute;
  width: 250px;
  height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: scale(1);

  & > div.line {
    position: absolute;
    width: 350px;
    height: 3px;
    animation: ${QRScannerLineAnimation} 5s ease-in-out infinite;

    background: linear-gradient(
      270deg,
      rgba(229, 89, 65, 0) 0%,
      ${({ theme }) => theme.primary} 50%,
      rgba(229, 89, 65, 0) 100%
    );
  }

  & > div.shadow {
    position: absolute;
    width: 250px;
    height: 250px;
    border-radius: 53px;
    overflow: hidden;

    &::before {
      content: "";
      display: block;
      position: absolute;
      bottom: 0;
      width: 100%;
      background: linear-gradient(
        to top,
        ${({ theme }) => transparentize(0.5, theme.primary)},
        rgba(229, 89, 65, 0)
      );
      animation: ${QRScannerLaserBefore} 5s ease-in-out infinite;
    }

    &::after {
      content: "";
      display: block;
      position: absolute;
      width: 100%;
      top: 0;
      background: linear-gradient(
        to bottom,
        ${({ theme }) => transparentize(0.5, theme.primary)},
        rgba(229, 89, 65, 0)
      );
      animation: ${QRScannerLaserAfter} 5s ease-in-out infinite;
    }
  }
`;

export const QRScannerText = styled.span`
  position: absolute;
  font-weight: 500;
  font-size: 20px;
  color: white;
  text-align: center;
  padding: 20px 40px;
  transform: translateY(200px);

  @media (max-height: 450px) {
    display: none;
  }
`;

export const QRScannerLogo = styled.a`
  position: absolute;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(300px);

  @media (max-height: 670px) {
    display: none;
  }
`;

export const QRScannerMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 20px;

  & > svg {
    width: 100px;
    height: 100px;
  }
`;

export const QRScannerPermissionTitle = styled.span`
  text-align: center;
  font-size: 25px;
  font-weight: 500;
  text-align: center;
  color: ${({ theme }) => theme.text};
  margin-top: 20px;
`;

export const QRScannerPermissionSubtitle = styled.span`
  color: ${({ theme }) => transparentize(0.4, theme.text)};
  text-align: center;
  font-size: 15px;
  font-weight: 300;
  max-width: 600px;
  margin-top: 10px;
`;

export const QRScannerDownloadButton = styled.button`
  font-size: 18px;
  font-weight: 500;
  margin-top: 20px;
  border: none;
  color: ${({ theme }) => theme.text};
  background: ${({ theme }) => theme.primary};
  display: flex;
  justify-content: center;
  align-items: center;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;

  & > svg.icon {
    height: 18px;
    width: 18px;
    fill: ${({ theme }) => theme.text};
  }
`;

export const QRScannerStepButton = styled.button`
  color: ${({ theme }) => theme.primary};
  font-size: 16px;
  font-weight: 500;
  margin-top: 20px;
  border: none;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  & > svg.icon {
    height: 25px;
    width: 10px;
    fill: ${({ theme }) => theme.primary};
  }
`;

export const QRScannerInputContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
  height: 40px;
  margin-top: 30px;
  width: 100%;

  & > ${ButtonContainer} {
    border-radius: 15px;
    & > svg.icon {
      height: 18px;
      width: 11px;
    }
  }
`;

export const QRScannerInput = styled.input`
  color: ${({ theme }) => theme.text};
  background: ${({ theme }) => theme.background_light};
  border: none;
  text-align: center;
  max-width: 550px;
  width: 100%;
  padding: 5px 15px;
  border-radius: 15px;
  letter-spacing: 3px;
  font-size: 20px;
  font-weight: 500;
`;

interface QRScannerResultLoadingProps {
  opaque?: boolean;
}

export const QRScannerResultLoading = styled.div<QRScannerResultLoadingProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${({ theme, opaque }) =>
    transparentize(opaque ? 0.4 : 0, theme.background)};
  z-index: 100;

  body[data-reduced-motion~="false"] & {
    backdrop-filter: blur(5px);
  }
`;
